/**
 * Unified Employee Assignment Logic
 * Eliminates duplication across DraggableShift, BulkShiftOperations, and various dialogs
 * Provides consistent employee assignment validation and operations
 */

import { toast } from '@/hooks/ui/use-toast';
import { Employee, DayShift } from '@/types';

// Assignment result types
export interface AssignmentResult {
  success: boolean;
  message: string;
  conflicts?: ConflictInfo[];
  assignedEmployeeId?: string | null;
}

export interface ConflictInfo {
  shiftId: string;
  title?: string;
  startTime: Date;
  endTime: Date;
  employeeId: string;
  employeeName?: string;
}

export interface AssignmentValidationResult {
  isValid: boolean;
  canAssign: boolean;
  conflicts: ConflictInfo[];
  warnings: string[];
  errors: string[];
}

// Assignment operation types
export type AssignmentOperation = 'assign' | 'unassign' | 'bulk-assign' | 'drag-assign';

export interface AssignmentOptions {
  operation: AssignmentOperation;
  validateAvailability?: boolean;
  allowOverlap?: boolean;
  showToast?: boolean;
  debounceMs?: number;
  context?: string; // For logging/debugging
}

/**
 * Core assignment validation service
 * Replaces duplicated availability checking across components
 */
class UnifiedEmployeeAssignmentService {
  private static instance: UnifiedEmployeeAssignmentService;
  private assignmentTimeouts = new Map<string, NodeJS.Timeout>();

  static getInstance(): UnifiedEmployeeAssignmentService {
    if (!this.instance) {
      this.instance = new UnifiedEmployeeAssignmentService();
    }
    return this.instance;
  }

  /**
   * Validate employee assignment before execution
   * Consolidates availability checking logic
   */
  async validateAssignment(
    employeeId: string | null,
    shiftId: string,
    startTime: Date,
    endTime: Date,
    allShifts: DayShift[],
    employees: Employee[]
  ): Promise<AssignmentValidationResult> {
    const result: AssignmentValidationResult = {
      isValid: true,
      canAssign: true,
      conflicts: [],
      warnings: [],
      errors: []
    };

    // Handle unassignment case
    if (!employeeId) {
      result.warnings.push('Shift will be unassigned');
      return result;
    }

    // Find employee
    const employee = employees.find(e => e.id === employeeId);
    if (!employee) {
      result.isValid = false;
      result.canAssign = false;
      result.errors.push('Employee not found');
      return result;
    }

    // Check for conflicts
    const conflicts = this.findConflictingShifts(
      employeeId,
      startTime,
      endTime,
      allShifts,
      shiftId
    );

    if (conflicts.length > 0) {
      result.conflicts = conflicts;
      result.canAssign = false;
      result.errors.push(
        `${employee.name} has ${conflicts.length} conflicting shift(s)`
      );
    }

    // Check business rules (if needed)
    const businessRuleWarnings = await this.checkBusinessRules(
      employeeId,
      startTime,
      endTime,
      employee
    );
    
    result.warnings.push(...businessRuleWarnings);

    return result;
  }

  /**
   * Execute employee assignment with validation
   * Replaces duplicated assignment logic
   */
  async executeAssignment(
    shiftId: string,
    employeeId: string | null,
    onAssignEmployee: (shiftId: string, employeeId: string | null) => Promise<void>,
    shift: { startTime: Date; endTime: Date; title?: string },
    allShifts: DayShift[],
    employees: Employee[],
    options: AssignmentOptions = {
      operation: 'assign',
      validateAvailability: true,
      showToast: true,
      debounceMs: 300
    }
  ): Promise<AssignmentResult> {
    const { 
      operation, 
      validateAvailability = true, 
      showToast = true, 
      debounceMs = 300,
      context = 'unknown'
    } = options;

    // Clear existing timeout for this shift
    const existingTimeout = this.assignmentTimeouts.get(shiftId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    return new Promise((resolve) => {
      const timeoutId = setTimeout(async () => {
        try {
          console.log(`🔄 UnifiedAssignment (${operation}): Starting assignment`, {
            shiftId,
            employeeId,
            context,
            shiftTime: `${shift.startTime.toISOString()} - ${shift.endTime.toISOString()}`
          });

          // Validate assignment if required
          if (validateAvailability && employeeId) {
            const validation = await this.validateAssignment(
              employeeId,
              shiftId,
              shift.startTime,
              shift.endTime,
              allShifts,
              employees
            );

            if (!validation.canAssign) {
              const employee = employees.find(e => e.id === employeeId);
              const conflictDetails = validation.conflicts.length > 0
                ? `Conflicts: ${validation.conflicts.map(c => c.title || 'Unnamed shift').join(', ')}`
                : 'Time conflict detected';

              console.log('❌ UnifiedAssignment: Assignment blocked - employee unavailable:', {
                employeeId,
                conflicts: validation.conflicts,
                errors: validation.errors
              });

              if (showToast) {
                toast.error('Employee Unavailable', {
                  description: `${employee?.name || 'Employee'} is not available. ${conflictDetails}`,
                });
              }

              resolve({
                success: false,
                message: `Assignment failed: ${validation.errors.join(', ')}`,
                conflicts: validation.conflicts
              });
              return;
            }
          }

          // Execute the assignment
          await onAssignEmployee(shiftId, employeeId);

          // Invalidate cache if available
          try {
            const { employeeAvailabilityCache } = await import('@/lib/employees/EmployeeAvailabilityCache');
            const currentShift = allShifts.find(s => s.id === shiftId);
            const affectedEmployees = [employeeId, currentShift?.employeeId].filter(Boolean) as string[];
            
            employeeAvailabilityCache.invalidateCache(
              `assignment (${operation})`,
              affectedEmployees
            );
          } catch (error) {
            console.warn('Cache invalidation failed:', error);
          }

          console.log('✅ UnifiedAssignment: Assignment completed successfully');

          const employee = employees.find(e => e.id === employeeId);
          const successMessage = employeeId
            ? `${employee?.name || 'Employee'} assigned to shift`
            : 'Shift unassigned';

          if (showToast) {
            toast.success(successMessage);
          }

          resolve({
            success: true,
            message: successMessage,
            assignedEmployeeId: employeeId
          });

        } catch (error) {
          console.error(`UnifiedAssignment: Assignment failed (${operation}):`, error);
          
          if (showToast) {
            toast.error('Assignment Failed', {
              description: 'Failed to assign employee to shift. Please try again.',
            });
          }

          resolve({
            success: false,
            message: `Assignment failed: ${error instanceof Error ? error.message : 'Unknown error'}`
          });
        } finally {
          this.assignmentTimeouts.delete(shiftId);
        }
      }, debounceMs);

      this.assignmentTimeouts.set(shiftId, timeoutId);
    });
  }

  /**
   * Execute bulk assignment operations
   * Replaces BulkShiftOperations assignment logic
   */
  async executeBulkAssignment(
    shiftIds: string[],
    employeeId: string | null,
    onBulkAssignEmployee: (shiftIds: string[], employeeId: string | null) => Promise<void>,
    shifts: DayShift[],
    employees: Employee[],
    options: Omit<AssignmentOptions, 'operation'> = {}
  ): Promise<AssignmentResult> {
    const { showToast = true } = options;

    if (shiftIds.length === 0) {
      const message = 'No shifts selected for assignment';
      if (showToast) {
        toast.error(message);
      }
      return { success: false, message };
    }

    try {
      console.log('🔄 UnifiedAssignment (bulk): Starting bulk assignment', {
        shiftCount: shiftIds.length,
        employeeId
      });

      // Validate all assignments if employee is being assigned
      if (employeeId) {
        const validationResults = await Promise.all(
          shiftIds.map(async (shiftId) => {
            const shift = shifts.find(s => s.id === shiftId);
            if (!shift) return { shiftId, isValid: false, errors: ['Shift not found'] };

            const validation = await this.validateAssignment(
              employeeId,
              shiftId,
              shift.startTime,
              shift.endTime,
              shifts,
              employees
            );

            return { shiftId, ...validation };
          })
        );

        const invalidAssignments = validationResults.filter(r => !r.canAssign);
        if (invalidAssignments.length > 0) {
          const employee = employees.find(e => e.id === employeeId);
          const errorMessage = `${invalidAssignments.length} of ${shiftIds.length} assignments would create conflicts`;
          
          if (showToast) {
            toast.error('Bulk Assignment Conflicts', {
              description: `${employee?.name || 'Employee'} has conflicts with some shifts. Please resolve conflicts first.`,
            });
          }

          return {
            success: false,
            message: errorMessage,
            conflicts: invalidAssignments.flatMap(r => r.conflicts || [])
          };
        }
      }

      // Execute bulk assignment
      await onBulkAssignEmployee(shiftIds, employeeId);

      // Invalidate cache
      try {
        const { employeeAvailabilityCache } = await import('@/lib/employees/EmployeeAvailabilityCache');
        const affectedEmployees = new Set<string>();
        
        // Add new employee
        if (employeeId) affectedEmployees.add(employeeId);
        
        // Add previously assigned employees
        shiftIds.forEach(shiftId => {
          const shift = shifts.find(s => s.id === shiftId);
          if (shift?.employeeId) affectedEmployees.add(shift.employeeId);
        });

        employeeAvailabilityCache.invalidateCache(
          'bulk assignment',
          Array.from(affectedEmployees)
        );
      } catch (error) {
        console.warn('Cache invalidation failed:', error);
      }

      const employee = employees.find(e => e.id === employeeId);
      const successMessage = employeeId
        ? `${shiftIds.length} shifts assigned to ${employee?.name || 'Employee'}`
        : `${shiftIds.length} shifts unassigned`;

      if (showToast) {
        toast.success(successMessage);
      }

      console.log('✅ UnifiedAssignment (bulk): Bulk assignment completed successfully');

      return {
        success: true,
        message: successMessage,
        assignedEmployeeId: employeeId
      };

    } catch (error) {
      console.error('UnifiedAssignment (bulk): Bulk assignment failed:', error);
      
      const errorMessage = 'Failed to assign shifts';
      if (showToast) {
        toast.error(errorMessage);
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * Find conflicting shifts for an employee
   * Consolidates conflict detection logic
   */
  private findConflictingShifts(
    employeeId: string,
    startTime: Date,
    endTime: Date,
    allShifts: DayShift[],
    excludeShiftId?: string
  ): ConflictInfo[] {
    return allShifts
      .filter(shift => 
        shift.employeeId === employeeId &&
        shift.id !== excludeShiftId &&
        this.doTimesOverlap(startTime, endTime, shift.startTime, shift.endTime)
      )
      .map(shift => ({
        shiftId: shift.id,
        title: shift.title,
        startTime: shift.startTime,
        endTime: shift.endTime,
        employeeId: shift.employeeId!,
        employeeName: shift.employeeName
      }));
  }

  /**
   * Check if two time ranges overlap
   */
  private doTimesOverlap(
    start1: Date,
    end1: Date,
    start2: Date,
    end2: Date
  ): boolean {
    return start1 < end2 && start2 < end1;
  }

  /**
   * Check business rules for assignment
   * Extensible for future business logic
   */
  private async checkBusinessRules(
    employeeId: string,
    startTime: Date,
    endTime: Date,
    employee: Employee
  ): Promise<string[]> {
    const warnings: string[] = [];

    // Example: Check shift length
    const shiftHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
    if (shiftHours > 8) {
      warnings.push('Shift exceeds 8 hours - may require overtime approval');
    }

    // Add more business rules as needed
    return warnings;
  }

  /**
   * Cleanup timeouts on service destruction
   */
  cleanup(): void {
    this.assignmentTimeouts.forEach(timeout => clearTimeout(timeout));
    this.assignmentTimeouts.clear();
  }
}

// Export singleton instance
export const unifiedAssignmentService = UnifiedEmployeeAssignmentService.getInstance();

// Convenience wrapper functions
export const validateEmployeeAssignment = (
  employeeId: string | null,
  shiftId: string,
  startTime: Date,
  endTime: Date,
  allShifts: DayShift[],
  employees: Employee[]
) => unifiedAssignmentService.validateAssignment(employeeId, shiftId, startTime, endTime, allShifts, employees);

export const executeEmployeeAssignment = (
  shiftId: string,
  employeeId: string | null,
  onAssignEmployee: (shiftId: string, employeeId: string | null) => Promise<void>,
  shift: { startTime: Date; endTime: Date; title?: string },
  allShifts: DayShift[],
  employees: Employee[],
  options?: AssignmentOptions
) => unifiedAssignmentService.executeAssignment(shiftId, employeeId, onAssignEmployee, shift, allShifts, employees, options);

export const executeBulkEmployeeAssignment = (
  shiftIds: string[],
  employeeId: string | null,
  onBulkAssignEmployee: (shiftIds: string[], employeeId: string | null) => Promise<void>,
  shifts: DayShift[],
  employees: Employee[],
  options?: Omit<AssignmentOptions, 'operation'>
) => unifiedAssignmentService.executeBulkAssignment(shiftIds, employeeId, onBulkAssignEmployee, shifts, employees, options);

// Export service for advanced usage
export { UnifiedEmployeeAssignmentService };